import React, { useState, useRef, useEffect } from 'react';

const CustomFilterDropdown = ({
  options,
  selectedValue,
  onChange,
  onScrollEnd,
  isLoading,
  placeholder,
  className,
  displayField = 'title',
  formik = false,
  setFormikField = null,
  width = { min: '200px' },
  showSearch = true
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [filteredOptions, setFilteredOptions] = useState([]);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const dropdownRef = useRef(null);
  const searchRef = useRef(null);
  const optionsContainerRef = useRef(null);
  const invisibleSearchRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      // Focus invisible search input if search is not shown
      if (!showSearch && invisibleSearchRef.current) {
        invisibleSearchRef.current.focus();
      } else if (searchRef.current) {
        searchRef.current.focus();
      }
    }
  }, [isOpen, showSearch]);

  useEffect(() => {
    setFilteredOptions(options || []);
  }, [options]);

  useEffect(() => {
    if (!searchInput) {
      setFilteredOptions(options || []);
      return;
    }

    const filtered = options?.filter((option) =>
      String(option[displayField] || '')
        .toLowerCase()
        .includes(searchInput.toLowerCase())
    );
    setFilteredOptions(filtered || []);
    setHighlightedIndex(-1); // Reset highlight when search changes
  }, [searchInput, options, displayField]);

  const handleScroll = (e) => {
    const bottom = e.target.scrollHeight - e.target.scrollTop <= e.target.clientHeight + 5;
    if (bottom && !isLoading && onScrollEnd) {
      onScrollEnd();
    }
  };

  const getDisplayValue = () => {
    if (!selectedValue) return placeholder;
    const selectedOption = options?.find((option) => option.id === selectedValue);
    return selectedOption ? selectedOption[displayField] : placeholder;
  };

  const handleClickOutside = (e) => {
    if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleChange = (id) => {
    if (formik && setFormikField) {
      setFormikField(id);
    } else {
      onChange(id);
    }
    setIsOpen(false);
    setSearchInput('');
  };

  const handleKeyDown = (e) => {
    if (!isOpen) {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
        e.preventDefault();
        setIsOpen(true);
      }
      return;
    }

    const optionsLength = filteredOptions.length + 1; // +1 for the placeholder option

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex((prev) => (prev < optionsLength - 1 ? prev + 1 : 0));
        if (optionsContainerRef.current && highlightedIndex >= 0) {
          const highlightedEl = optionsContainerRef.current.children[highlightedIndex + 1];
          if (highlightedEl) {
            highlightedEl.scrollIntoView({ block: 'nearest' });
          }
        }
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : optionsLength - 1));
        if (optionsContainerRef.current && highlightedIndex >= 0) {
          const highlightedEl = optionsContainerRef.current.children[highlightedIndex - 1];
          if (highlightedEl) {
            highlightedEl.scrollIntoView({ block: 'nearest' });
          }
        }
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex === -1) {
          // Do nothing if no option is highlighted
        } else if (highlightedIndex === 0) {
          handleChange(null); // Select placeholder
        } else {
          handleChange(filteredOptions[highlightedIndex - 1].id);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        break;
      default:
        // Allow typing for search even when search box is hidden
        if (!showSearch && e.key.length === 1) {
          setSearchInput(prev => prev + e.key);
        }
        break;
    }
  };

  return (
    <div
      className="position-relative"
      ref={dropdownRef}
      tabIndex={0}
      onKeyDown={handleKeyDown}
    >
      <div
        onClick={() => setIsOpen(!isOpen)}
        className={className ? className : "form-select form-select-solid text-muted fs-7 h-40px d-flex align-items-center justify-content-between"}
          style={{
          minWidth: width.min,
          // maxWidth: width.max,
          cursor: 'pointer'
        }}

      >
        <span>{getDisplayValue()}</span>
        {/* <span className="fa fa-chevron-down fs-9"></span> */}
      </div>

      {isOpen && (
        <div
          className="position-absolute start-0 top-100 z-index-3  w-100 mt-1 rounded shadow"
          style={{ maxHeight: '300px', minWidth: '200px', background:'#F5F8FA' }}
        >
          {/* Invisible search input for keyboard entry when search box is hidden */}
          {!showSearch && (
            <input
              ref={invisibleSearchRef}
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              aria-hidden="true"
              style={{ position: 'absolute', opacity: 0, height: 0 }}
              onBlur={() => {}} // Prevent losing focus
            />
          )}
          
          {/* Visible search Input - conditionally rendered */}
          {showSearch && (
            <div className="px-3 fs-6 py-2 border-bottom">
              <input
                ref={searchRef}
                type="text"
                className="form-control form-control-sm"
                placeholder={`Search`}
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          )}

          {/* Options List */}
          <div
            ref={optionsContainerRef}
            style={{ maxHeight: '250px', overflowY: 'auto' }}
            onScroll={handleScroll}
          >
            <div
              className={`py-2 px-3 fs-6 cursor-pointer option-item ${
                highlightedIndex === 0 ? 'highlighted' : ''
              }`}
              onClick={() => handleChange(null)}
              onMouseEnter={() => setHighlightedIndex(0)}
              onMouseLeave={() => setHighlightedIndex(-1)}
            >
              {placeholder}
            </div>

            {filteredOptions?.length > 0 ? (
              filteredOptions.map((option, index) => (
                <div
                  key={index}
                  className={`py-2 px-3 fs-6 cursor-pointer option-item ${
                    highlightedIndex === index + 1 ? 'highlighted' : ''
                  }`}
                  onClick={() => handleChange(option.id)}
                  onMouseEnter={() => setHighlightedIndex(index + 1)}
                  onMouseLeave={() => setHighlightedIndex(-1)}
                >
                  {option[displayField]}
                </div>
              ))
            ) : (
              <div className="py-2 px-3 text-center text-muted">
                No items found
              </div>
            )}

            {isLoading && (
              <div className="py-2 px-3 text-center">
                <span className="spinner-border spinner-border-sm text-primary" role="status"></span>
                <span className="ms-2">Loading...</span>
              </div>
            )}
          </div>
        </div>
      )}
      
      <style jsx>{`
        .option-item:hover {
          background-color: #1967D2 !important;
          color: #ffffff !important;
        }
        .highlighted {
          background-color: #1967D2 !important;
          color: #ffffff !important;
        }
      `}</style>
    </div>
  );
};

export default CustomFilterDropdown; 